/** Configuração e prévia do catálogo N3 global (Pipefy). */

import { supabase } from "@/lib/supabase/client";
import { pipefyApi } from "@/lib/api-client";

export const N3_CATALOG_CONFIG_STORAGE_KEY = "solis_pipefy_n3_catalog_config_v1";
export const N3_CATALOG_CONFIG_TABLE = "solis_pipefy_catalogo_n3_config" as const;

/** Database global de tipos N1/N2/N3 (mesmo ID usado em SubtiposDespesaTab / Sincronizar). */
export const DEFAULT_TIPOS_DATABASE_ID = "-2v28bXC";

/** Nome sugerido ao criar o database no Pipefy (único para todos os anos). */
export const N3_CATALOG_DATABASE_DEFAULT_NAME = "Catálogo N3 — Global";
export const N3_CATALOG_DATABASE_NAME_PREFIX = "Catálogo N3";

export type N3CatalogSourceMode = "orcamento" | "realizado_sem_orcamento";

export interface N3CatalogConfig {
  n3CatalogTableId: string;
  n3CatalogTableName: string;
  tiposTableId: string;
  ano: number;
  sourceMode: N3CatalogSourceMode;
  allowCreate: boolean;
  allowDeactivate: boolean;
}

export interface N3CatalogDbRow {
  id: number;
  n3_catalog_table_id: string | null;
  n3_catalog_table_name: string | null;
  tipos_table_id: string;
  updated_at: string;
}

export interface N3CatalogPreview {
  ano: number;
  sourceMode: N3CatalogSourceMode;
  /** N3 na view hierarquia (sem filtro de valor). */
  noOrcadoBruto: number;
  /** N3 elegíveis para o catálogo Pipefy (sem resultado financeiro; com valor ≠ 0 no orçado). */
  noOrcado: number;
  excluidosResultadoFinanceiro: number;
  excluidosValorZerado: number;
  noMapaAtivo: number;
  noMapaInativo: number;
  semRegistroNoMapa: number;
  amostraSemMapa: { chave_externa: string; nome_tipo: string }[];
}

/** Normalização alinhada a `solis_normalizar_chave_texto` (partes da chave N3). */
export function normChavePart(v: string): string {
  return v
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim()
    .replace(/\s+/g, " ");
}

export function buildN3ChaveExterna(n1: string, n2: string, n3: string): string {
  return `n3:${normChavePart(n1)}|${normChavePart(n2)}|${normChavePart(n3)}`;
}

export function n1NormFromN3Chave(chaveExterna: string): string {
  const rest = String(chaveExterna || "")
    .trim()
    .replace(/^n3:/i, "");
  return rest.split("|")[0] || "";
}

/** Chave N2 (`n2:…`) a partir do N3 e do `chave_pai` da hierarquia. */
export function n2ChaveFromN3(chaveN3: string, chavePai: string | null | undefined): string | null {
  const pai = String(chavePai || "").trim();
  if (pai.startsWith("n2:")) return pai;
  const parts = String(chaveN3 || "")
    .trim()
    .replace(/^n3:/i, "")
    .split("|");
  if (parts.length < 2 || !parts[0] || !parts[1]) return null;
  return `n2:${parts[0]}|${parts[1]}`;
}

/** Chave N1 (`n1:…`) a partir do N3 / pai N2. */
export function n1ChaveFromN3(chaveN3: string, chavePai: string | null | undefined): string | null {
  const n2 = n2ChaveFromN3(chaveN3, chavePai);
  if (n2) {
    const n1Part = n2.replace(/^n2:/i, "").split("|")[0];
    if (n1Part) return `n1:${n1Part}`;
  }
  const n1Part = n1NormFromN3Chave(chaveN3);
  return n1Part ? `n1:${n1Part}` : null;
}

export function isN3ExcludedFromPipefyCatalog(chaveExterna: string): boolean {
  return n1NormFromN3Chave(chaveExterna).includes("resultado financeiro");
}

export function parseAnoFromMes(mes: string | null | undefined): number | null {
  if (!mes) return null;
  const s = String(mes).trim();
  if (s.includes("-")) {
    const y = parseInt(s.split("-")[0], 10);
    return Number.isFinite(y) ? y : null;
  }
  if (s.includes("/")) {
    const y = parseInt(s.split("/")[2], 10);
    return Number.isFinite(y) ? y : null;
  }
  return null;
}

/** Chaves N3 com soma de valor ≠ 0 no orçado do ano (solis_orcamento). */
export async function loadNonzeroN3ChavesOrcamento(ano: number): Promise<Set<string>> {
  const sums = new Map<string, number>();
  let from = 0;
  const pageSize = 1000;

  while (true) {
    const { data, error } = await supabase
      .from("solis_orcamento")
      .select("tipo_receita_despesa, classificacao_receita_despesa, descricao_receita_despesa, mes, valor")
      .range(from, from + pageSize - 1);
    if (error) throw error;
    if (!data?.length) break;

    for (const row of data) {
      if (parseAnoFromMes(row.mes as string) !== ano) continue;
      const n1 = String(row.tipo_receita_despesa || "").trim();
      const n2 = String(row.classificacao_receita_despesa || "").trim();
      const n3 = String(row.descricao_receita_despesa || "").trim();
      if (!n1 || !n2 || !n3) continue;
      const chave = buildN3ChaveExterna(n1, n2, n3);
      const v = Number(row.valor) || 0;
      sums.set(chave, (sums.get(chave) || 0) + v);
    }

    if (data.length < pageSize) break;
    from += pageSize;
  }

  const keys = new Set<string>();
  for (const [chave, total] of sums) {
    if (total !== 0) keys.add(chave);
  }
  return keys;
}

export type N3HierarquiaRow = {
  chave_externa: string;
  nome_tipo?: string;
};

/** Filtra N3 para alimentar o catálogo Pipefy (regra de negócio do orçado). */
export function filterN3RowsForPipefyCatalog<T extends N3HierarquiaRow>(
  rows: T[],
  options: {
    sourceMode: N3CatalogSourceMode;
    nonzeroOrcamentoKeys?: Set<string> | null;
  }
): { rows: T[]; excluidosResultadoFinanceiro: number; excluidosValorZerado: number } {
  let exclFinanceiro = 0;
  let exclZero = 0;
  const out: T[] = [];

  for (const row of rows) {
    const chave = String(row.chave_externa || "").trim();
    if (!chave) continue;

    if (isN3ExcludedFromPipefyCatalog(chave)) {
      exclFinanceiro++;
      continue;
    }

    if (options.sourceMode === "orcamento" && options.nonzeroOrcamentoKeys) {
      if (!options.nonzeroOrcamentoKeys.has(chave)) {
        exclZero++;
        continue;
      }
    }

    out.push(row);
  }

  return {
    rows: out,
    excluidosResultadoFinanceiro: exclFinanceiro,
    excluidosValorZerado: exclZero,
  };
}

export interface N3CatalogSyncResult {
  success: boolean;
  error?: string;
  ano?: number;
  sourceMode?: string;
  sourceCount?: number;
  created?: number;
  updated?: number;
  deactivated?: number;
  skippedNoMap?: number;
  createdItems?: string[];
  updatedItems?: string[];
  deactivatedItems?: string[];
  warnings?: Record<string, boolean>;
  errors?: string[];
}

export interface CampoPipefyConfig {
  label: string;
  type: "short_text" | "connector_table";
  required: boolean;
  description?: string;
  connectedRepoId?: string;
}

const SOURCE_VIEW: Record<N3CatalogSourceMode, string> = {
  orcamento: "solis_vw_tipos_despesa_hierarquia_v1",
  realizado_sem_orcamento: "solis_vw_tipos_despesa_hierarquia_realizado_sem_orcamento_v1",
};

export function getDefaultN3CatalogConfig(): N3CatalogConfig {
  const year = new Date().getFullYear();
  return {
    n3CatalogTableId: "",
    n3CatalogTableName: "",
    tiposTableId: DEFAULT_TIPOS_DATABASE_ID,
    ano: year,
    sourceMode: "orcamento",
    allowCreate: true,
    allowDeactivate: true,
  };
}

export function loadN3CatalogConfigLocal(): N3CatalogConfig {
  try {
    const raw = localStorage.getItem(N3_CATALOG_CONFIG_STORAGE_KEY);
    if (!raw) return getDefaultN3CatalogConfig();
    const parsed = JSON.parse(raw) as Partial<N3CatalogConfig>;
    return { ...getDefaultN3CatalogConfig(), ...parsed };
  } catch {
    return getDefaultN3CatalogConfig();
  }
}

export function saveN3CatalogConfigLocal(config: N3CatalogConfig): void {
  localStorage.setItem(N3_CATALOG_CONFIG_STORAGE_KEY, JSON.stringify(config));
}

export async function loadN3CatalogConfigFromDb(): Promise<{
  config: N3CatalogConfig;
  fromDb: boolean;
  tableMissing: boolean;
}> {
  const base = loadN3CatalogConfigLocal();
  const { data, error } = await supabase
    .from(N3_CATALOG_CONFIG_TABLE)
    .select("n3_catalog_table_id, n3_catalog_table_name, tipos_table_id")
    .eq("id", 1)
    .maybeSingle();

  if (error) {
    const missing =
      error.message.includes("does not exist") || error.message.includes("schema cache");
    if (missing) {
      return { config: base, fromDb: false, tableMissing: true };
    }
    throw error;
  }

  if (!data) {
    return { config: base, fromDb: false, tableMissing: false };
  }

  const merged: N3CatalogConfig = {
    ...base,
    n3CatalogTableId: data.n3_catalog_table_id?.trim() ?? "",
    n3CatalogTableName: data.n3_catalog_table_name?.trim() ?? "",
    tiposTableId: data.tipos_table_id?.trim() || DEFAULT_TIPOS_DATABASE_ID,
  };
  saveN3CatalogConfigLocal(merged);
  return { config: merged, fromDb: true, tableMissing: false };
}

export async function saveN3CatalogIdsToDb(payload: {
  n3CatalogTableId: string;
  n3CatalogTableName?: string;
  tiposTableId: string;
}): Promise<void> {
  const { error } = await supabase.from(N3_CATALOG_CONFIG_TABLE).upsert(
    {
      id: 1,
      n3_catalog_table_id: payload.n3CatalogTableId.trim() || null,
      n3_catalog_table_name: payload.n3CatalogTableName?.trim() || null,
      tipos_table_id: payload.tiposTableId.trim() || DEFAULT_TIPOS_DATABASE_ID,
      updated_at: new Date().toISOString(),
    },
    { onConflict: "id" }
  );
  if (error) {
    if (error.message.includes("does not exist") || error.message.includes("schema cache")) {
      throw new Error(
        "Tabela solis_pipefy_catalogo_n3_config não encontrada. Aplique a migração 096 no Supabase."
      );
    }
    throw error;
  }
}

export function sourceViewName(mode: N3CatalogSourceMode): string {
  return SOURCE_VIEW[mode];
}

/** Campos do database catálogo N3 (estrutura alinhada ao legado de subtipos, sem centro de custo). */
export function getN3CatalogPipefyFields(tiposTableId: string): CampoPipefyConfig[] {
  return [
    {
      label: "Nome",
      type: "short_text",
      required: true,
      description: "Descrição N3 (subtipo / linha do orçado)",
    },
    {
      label: "Chave externa",
      type: "short_text",
      required: true,
      description: "Chave n3:… do mapa global de tipos",
    },
    {
      label: "Tipo de Despesa (N3)",
      type: "connector_table",
      required: true,
      description: "Registro N3 (subtipo) no database global de tipos",
      connectedRepoId: tiposTableId,
    },
    {
      label: "Tipo de Despesa (N2)",
      type: "connector_table",
      required: false,
      description: "Classificação (pai do N3) no database global de tipos",
      connectedRepoId: tiposTableId,
    },
    {
      label: "Tipo de Despesa (N1)",
      type: "connector_table",
      required: false,
      description: "Categoria (avô do N3) no database global de tipos",
      connectedRepoId: tiposTableId,
    },
  ];
}

export async function createN3CatalogPipefyDatabase(options: {
  name: string;
  description?: string;
  tiposTableId: string;
}): Promise<{ tableId: string; tableName: string }> {
  const createResult = await pipefyApi.createDatabase({
    name: options.name,
    description: options.description,
  });

  if (!createResult.success || !createResult.table?.id) {
    throw new Error(createResult.error || "Erro ao criar database no Pipefy");
  }

  const tableId = createResult.table.id as string;
  const tableName = (createResult.table.name as string) || options.name;
  const campos = getN3CatalogPipefyFields(options.tiposTableId);

  for (const campo of campos) {
    try {
      if (campo.type === "connector_table") {
        await pipefyApi.createField({
          tableId,
          type: "connector",
          label: campo.label,
          required: campo.required,
          description: campo.description,
          connectedRepoId: campo.connectedRepoId,
        });
      } else {
        await pipefyApi.createField({
          tableId,
          type: campo.type,
          label: campo.label,
          required: campo.required,
          description: campo.description,
        });
      }
    } catch (e) {
      console.warn(`Campo "${campo.label}":`, e);
    }
  }

  return { tableId, tableName };
}
